import { useEffect, useRef, useState } from "react"
import { useParams, useNavigate } from "react-router-dom"
import { Loader2, Trash2, ImagePlus, Pencil, ZoomIn, X } from "lucide-react"
import { toast } from "sonner"
import { useForm } from "react-hook-form"
import { zodResolver } from "@hookform/resolvers/zod"
import { z } from "zod"

import { APP_NAME } from "@/lib/appConfig"
import {
  useMaintenanceIssue,
  useUpdateIssue,
  useDeleteIssue,
  useUploadMaintenancePhotos,
  useDeleteMaintenancePhoto,
} from "@/features/maintenance/hooks/useMaintenance"
import { PageHeader } from "@/components/shared/PageHeader"
import { StatusBadge } from "@/components/shared/StatusBadge"
import { ListSkeleton } from "@/components/shared/LoadingSkeleton"
import { Card, CardContent } from "@/components/ui/card"
import { Button } from "@/components/ui/button"
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog"
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet"
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form"
import { Input } from "@/components/ui/input"
import { Textarea } from "@/components/ui/textarea"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"

function formatDate(dateStr?: string | null) {
  if (!dateStr) return "—"
  return new Date(dateStr).toLocaleDateString(undefined, {
    day: "numeric",
    month: "short",
    year: "numeric",
  })
}

const editSchema = z.object({
  title: z.string().min(1, "Title is required"),
  description: z.string().optional(),
  priority: z.string().min(1),
  category: z.string().optional(),
  scheduled_date: z.string().optional(),
  estimated_cost: z.string().optional(),
  currency: z.string().optional(),
})
type EditFormValues = z.infer<typeof editSchema>

function EditSheet({
  open,
  onOpenChange,
  issue,
}: {
  open: boolean
  onOpenChange: (v: boolean) => void
  issue: NonNullable<ReturnType<typeof useMaintenanceIssue>["data"]>
}) {
  const updateIssue = useUpdateIssue()

  const form = useForm<EditFormValues>({
    resolver: zodResolver(editSchema),
    defaultValues: {
      title: issue.title,
      description: issue.description ?? "",
      priority: issue.priority,
      category: issue.category ?? "",
      scheduled_date: issue.scheduled_date ?? "",
      estimated_cost: issue.estimated_cost != null ? String(issue.estimated_cost) : "",
      currency: issue.currency ?? "",
    },
  })

  function onSubmit(values: EditFormValues) {
    const payload: Record<string, unknown> = {
      title: values.title,
      priority: values.priority,
      property_id: issue.property_id,
    }
    if (values.description) payload.description = values.description
    if (values.category) payload.category = values.category
    if (values.scheduled_date) payload.scheduled_date = values.scheduled_date
    if (values.estimated_cost) payload.estimated_cost = Number(values.estimated_cost)
    if (values.currency) payload.currency = values.currency

    updateIssue.mutate(
      { id: issue.id, input: payload },
      {
        onSuccess: () => {
          toast.success("Issue updated")
          onOpenChange(false)
        },
        onError: () => toast.error("Failed to update issue"),
      }
    )
  }

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="bottom" className="h-[85dvh] overflow-y-auto rounded-t-2xl">
        <SheetHeader className="mb-4">
          <SheetTitle>Edit Maintenance Issue</SheetTitle>
        </SheetHeader>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4 pb-8">
            <FormField
              control={form.control}
              name="title"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Title *</FormLabel>
                  <FormControl>
                    <Input placeholder="e.g. Leaking tap in bathroom" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="description"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Description</FormLabel>
                  <FormControl>
                    <Textarea placeholder="Describe the issue..." rows={3} className="resize-none" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <div className="grid grid-cols-2 gap-3">
              <FormField
                control={form.control}
                name="priority"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Priority</FormLabel>
                    <Select onValueChange={field.onChange} value={field.value}>
                      <FormControl>
                        <SelectTrigger><SelectValue placeholder="Priority" /></SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        <SelectItem value="low">Low</SelectItem>
                        <SelectItem value="medium">Medium</SelectItem>
                        <SelectItem value="high">High</SelectItem>
                        <SelectItem value="urgent">Urgent</SelectItem>
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="category"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Category</FormLabel>
                    <FormControl>
                      <Input placeholder="e.g. Plumbing" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
            <FormField
              control={form.control}
              name="scheduled_date"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Scheduled Date</FormLabel>
                  <FormControl>
                    <Input type="date" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <div className="grid grid-cols-2 gap-3">
              <FormField
                control={form.control}
                name="estimated_cost"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Est. Cost</FormLabel>
                    <FormControl>
                      <Input type="number" min="0" step="0.01" placeholder="0.00" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="currency"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Currency</FormLabel>
                    <FormControl>
                      <Input placeholder="EUR" maxLength={3} {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
            <Button type="submit" className="w-full h-12 rounded-xl" disabled={updateIssue.isPending}>
              {updateIssue.isPending ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Saving...</> : "Save Changes"}
            </Button>
          </form>
        </Form>
      </SheetContent>
    </Sheet>
  )
}

function PhotoLightbox({ url, onClose }: { url: string; onClose: () => void }) {
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") onClose()
    }
    document.addEventListener("keydown", onKey)
    return () => document.removeEventListener("keydown", onKey)
  }, [onClose])

  return (
    <div
      className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center p-4"
      onClick={onClose}
    >
      <button
        className="absolute top-4 right-4 text-white/80 hover:text-white"
        onClick={onClose}
      >
        <X className="h-6 w-6" />
      </button>
      <img
        src={url}
        alt="Maintenance photo"
        className="max-w-full max-h-full object-contain rounded-lg"
        onClick={(e) => e.stopPropagation()}
      />
    </div>
  )
}

export default function MaintenanceDetailPage() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const { data: issue, isLoading, isError } = useMaintenanceIssue(id ?? "")
  const updateIssue = useUpdateIssue()
  const deleteIssue = useDeleteIssue()
  const uploadPhotos = useUploadMaintenancePhotos(id ?? "")
  const deletePhoto = useDeleteMaintenancePhoto(id ?? "")
  const fileInputRef = useRef<HTMLInputElement>(null)

  const [editOpen, setEditOpen] = useState(false)
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false)
  const [lightboxUrl, setLightboxUrl] = useState<string | null>(null)

  useEffect(() => {
    const prev = document.title
    document.title = `Maintenance Issue | ${APP_NAME}`
    return () => { document.title = prev }
  }, [])

  if (isLoading) return <ListSkeleton count={3} />

  if (isError || !issue) {
    return (
      <div className="flex flex-col h-full">
        <PageHeader title="Issue" back />
        <div className="flex-1 flex items-center justify-center">
          <p className="text-sm text-muted-foreground">Issue not found.</p>
        </div>
      </div>
    )
  }

  function updateStatus(status: "open" | "in_progress" | "completed") {
    if (!issue) return
    updateIssue.mutate(
      { id: issue.id, input: { status } },
      {
        onSuccess: () => toast.success("Status updated"),
        onError: () => toast.error("Failed to update status"),
      }
    )
  }

  function handleDelete() {
    if (!issue) return
    deleteIssue.mutate(issue.id, {
      onSuccess: () => {
        toast.success("Issue deleted")
        navigate("/maintenance")
      },
      onError: () => toast.error("Failed to delete issue"),
    })
  }

  function handleFileChange(e: React.ChangeEvent<HTMLInputElement>) {
    const files = Array.from(e.target.files ?? [])
    if (!files.length) return
    uploadPhotos.mutate(files, {
      onSuccess: () => toast.success("Photos uploaded"),
      onError: (err: unknown) => {
        const msg = (err as { response?: { data?: { error?: string } } })?.response?.data?.error
        toast.error(msg ?? "Failed to upload photos")
      },
    })
    e.target.value = ""
  }

  function handleDeletePhoto(photoId: string) {
    deletePhoto.mutate(photoId, {
      onSuccess: () => toast.success("Photo removed"),
      onError: () => toast.error("Failed to delete photo"),
    })
  }

  const status = issue.status
  const isCompleted = status === "completed"
  const photoCount = issue.photos?.length ?? 0

  return (
    <div className="flex flex-col h-full">
      <PageHeader title="Maintenance Issue" back />

      <div className="flex-1 overflow-y-auto pb-nav">
        <div className="px-4 pt-4 space-y-4">
          {/* Issue summary */}
          <Card className="rounded-2xl border-border">
            <CardContent className="p-4">
              <div className="flex items-start justify-between gap-2 mb-3">
                <h2 className="font-bold text-base">{issue.title}</h2>
                <StatusBadge status={issue.status} />
              </div>
              {!isCompleted && (
                <div className="flex gap-2 mb-3">
                  <Button variant="outline" size="sm" className="flex-1 h-9 rounded-xl gap-1 text-xs" onClick={() => setEditOpen(true)}>
                    <Pencil className="h-3.5 w-3.5" />
                    Edit
                  </Button>
                  <Button variant="outline" size="sm" className="flex-1 h-9 rounded-xl gap-1 text-xs border-destructive/30 text-destructive hover:bg-destructive/10 hover:text-destructive hover:border-destructive/50" onClick={() => setDeleteConfirmOpen(true)}>
                    <Trash2 className="h-3.5 w-3.5" />
                    Delete
                  </Button>
                </div>
              )}

              {issue.description && (
                <p className="text-sm text-muted-foreground mb-3">{issue.description}</p>
              )}

              <div className="grid grid-cols-2 gap-2 text-sm pt-3 border-t border-border">
                <div>
                  <p className="text-xs text-muted-foreground">Priority</p>
                  <StatusBadge status={issue.priority} />
                </div>
                {issue.property && (
                  <div>
                    <p className="text-xs text-muted-foreground">Property</p>
                    <p className="font-medium">{issue.property.name}</p>
                  </div>
                )}
                {issue.category && (
                  <div>
                    <p className="text-xs text-muted-foreground">Category</p>
                    <p className="font-medium">{issue.category}</p>
                  </div>
                )}
                {issue.scheduled_date && (
                  <div>
                    <p className="text-xs text-muted-foreground">Scheduled</p>
                    <p className="font-medium">{formatDate(issue.scheduled_date)}</p>
                  </div>
                )}
                {issue.completed_date && (
                  <div>
                    <p className="text-xs text-muted-foreground">Completed</p>
                    <p className="font-medium">{formatDate(issue.completed_date)}</p>
                  </div>
                )}
                {issue.estimated_cost != null && (
                  <div>
                    <p className="text-xs text-muted-foreground">Est. Cost</p>
                    <p className="font-medium">{issue.currency ?? ""} {issue.estimated_cost}</p>
                  </div>
                )}
                {issue.actual_cost != null && (
                  <div>
                    <p className="text-xs text-muted-foreground">Actual Cost</p>
                    <p className="font-medium">{issue.currency ?? ""} {issue.actual_cost}</p>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Status actions */}
          <Card className="rounded-2xl border-border">
            <CardContent className="p-4">
              <p className="text-sm font-medium mb-3">Update Status</p>
              <div className="flex gap-2 flex-wrap">
                {status !== "open" && (
                  <Button
                    variant="outline"
                    size="sm"
                    className="flex-1 h-9 rounded-xl text-xs"
                    onClick={() => updateStatus("open")}
                    disabled={updateIssue.isPending}
                  >
                    {updateIssue.isPending ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : null}
                    Re-open
                  </Button>
                )}
                {status !== "in_progress" && (
                  <Button
                    variant="outline"
                    size="sm"
                    className="flex-1 h-9 rounded-xl text-xs"
                    onClick={() => updateStatus("in_progress")}
                    disabled={updateIssue.isPending}
                  >
                    {updateIssue.isPending ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : null}
                    Mark In Progress
                  </Button>
                )}
                {status !== "completed" && (
                  <Button
                    size="sm"
                    className="flex-1 h-9 rounded-xl text-xs"
                    onClick={() => updateStatus("completed")}
                    disabled={updateIssue.isPending}
                  >
                    {updateIssue.isPending ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : null}
                    Mark Completed
                  </Button>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Contractor */}
          {issue.contractor && (
            <Card className="rounded-2xl border-border">
              <CardContent className="p-4">
                <p className="text-sm font-medium mb-2">Assigned Contractor</p>
                <p className="text-sm">{issue.contractor.name}</p>
              </CardContent>
            </Card>
          )}

          {/* Photos */}
          <Card className="rounded-2xl border-border">
            <CardContent className="p-4">
              <div className="flex items-center justify-between mb-3">
                <p className="text-sm font-medium">
                  Photos{photoCount > 0 ? ` (${photoCount}/5)` : ""}
                </p>
                {photoCount >= 5 ? (
                  <span className="text-xs text-amber-600 font-medium">Maximum 5 photos reached</span>
                ) : (
                  <Button
                    variant="outline"
                    size="sm"
                    className="h-8 rounded-lg gap-1 text-xs"
                    onClick={() => fileInputRef.current?.click()}
                    disabled={uploadPhotos.isPending}
                  >
                    {uploadPhotos.isPending ? (
                      <Loader2 className="h-3 w-3 animate-spin" />
                    ) : (
                      <ImagePlus className="h-3 w-3" />
                    )}
                    Add Photo
                  </Button>
                )}
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  multiple
                  className="hidden"
                  onChange={handleFileChange}
                />
              </div>

              {issue.photos && issue.photos.length > 0 ? (
                <div className="grid grid-cols-3 gap-2">
                  {issue.photos.map((photo) => (
                    <div key={photo.id} className="relative group aspect-square">
                      <img
                        src={photo.url}
                        alt="Maintenance photo"
                        className="w-full h-full object-cover rounded-lg cursor-pointer"
                        onClick={() => setLightboxUrl(photo.url)}
                      />
                      <div className="absolute top-1 right-1 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                        <button
                          className="bg-black/60 text-white rounded-full p-1"
                          onClick={() => setLightboxUrl(photo.url)}
                        >
                          <ZoomIn className="h-3 w-3" />
                        </button>
                        <button
                          className="bg-destructive text-destructive-foreground rounded-full p-1"
                          onClick={() => handleDeletePhoto(photo.id)}
                          disabled={deletePhoto.isPending}
                        >
                          <Trash2 className="h-3 w-3" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-muted-foreground">No photos yet. Tap "Add Photo" to upload.</p>
              )}
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Edit sheet */}
      {editOpen && (
        <EditSheet open={editOpen} onOpenChange={setEditOpen} issue={issue} />
      )}

      {/* Delete confirmation dialog */}
      <AlertDialog open={deleteConfirmOpen} onOpenChange={setDeleteConfirmOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this issue?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete "{issue.title}" and all its photos. This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={handleDelete}
              disabled={deleteIssue.isPending}
            >
              {deleteIssue.isPending ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : null}
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Photo lightbox */}
      {lightboxUrl && (
        <PhotoLightbox url={lightboxUrl} onClose={() => setLightboxUrl(null)} />
      )}
    </div>
  )
}
